import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  buildAverageRatingFromSectionBlob,
  formatAppraisalScoreDisplay,
  getRenderableSectionScoreEntries,
  resolveSectionGroupWeightage,
} from "pages/AppraisalManagment/utils/appraisalAverageRating";
import AverageRatingCalculationTable from "./component/AverageRatingCalculationTable";

const QuarterlyAppraisalView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isProbationaryView =
    location.pathname.includes("/probationary-appraisals") ||
    location.pathname.includes("/probational-appraisals");
  const recordFromState = location.state?.record;
  const recordFromStore = useSelector((state) => state.appraisalState?.appraisalProgressRecord);
  const record = recordFromState || recordFromStore || {};

  const monthOrder = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  };
  const months = Array.isArray(record.months)
    ? record.months
    : (typeof record.month === "string" ? record.month.split(",").map(m => m.trim()) : []);
  const monthDisplay = months
    .map((m) => String(m || "").trim().toLowerCase())
    .filter(Boolean)
    .sort((a, b) => (monthOrder[a] || 99) - (monthOrder[b] || 99))
    .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
    .join(", ");

  const quarterByMonth = {
    january: "Q1", february: "Q1", march: "Q1",
    april: "Q2", may: "Q2", june: "Q2",
    july: "Q3", august: "Q3", september: "Q3",
    october: "Q4", november: "Q4", december: "Q4",
  };
  const detectedQuarters = [...new Set(months.map(m => quarterByMonth[String(m).toLowerCase()]).filter(Boolean))];
  const quarter = detectedQuarters[0] || "";

  const sectionScoreBlob = record?.sections_data || record?.[F.sectionDataSynonym] || {};
  const overallScore = sectionScoreBlob?.overall_score ?? null;
  const normalizedMonths = months
    .map((m) => String(m || "").trim().toLowerCase())
    .filter(Boolean)
    .sort((a, b) => (monthOrder[a] || 99) - (monthOrder[b] || 99));
  const monthLabels = normalizedMonths.map((m) => m.charAt(0).toUpperCase() + m.slice(1));

  const getMonthScoreForGroup = (groupData, monthKey) => {
    const monthlyList =
      groupData?.monthly_scores_list ||
      groupData?.monthly_obtained_scores_list ||
      null;
    if (Array.isArray(monthlyList)) {
      const match = monthlyList.find((item) => String(item?.month || "").toLowerCase() === monthKey);
      if (match && match.obtained_score !== undefined && match.obtained_score !== null) {
        return match.obtained_score;
      }
    }

    const monthlyMap =
      groupData?.monthly_scores ||
      groupData?.monthly_obtained_scores ||
      groupData?.obtained_scores_by_month ||
      null;
    if (monthlyMap && typeof monthlyMap === "object") {
      const direct = monthlyMap[monthKey] ?? monthlyMap[monthKey.toLowerCase()];
      if (direct !== undefined && direct !== null && direct !== "") return direct;
    }
    return "-";
  };
  const navy = "#1A1B2F";
  const sectionBar = {
    background: navy,
    color: "#fff",
    padding: "10px 16px",
    fontWeight: 600,
    fontSize: "13px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    textAlign: "center",
  };
  const fieldBox = {
    background: "#F3F4F6",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    padding: "10px 12px",
    color: "#4B5563",
    fontSize: "14px",
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
  };
  const fieldLabel = {
    fontSize: "12px",
    color: "#111827",
    marginBottom: "6px",
    fontWeight: 600,
  };
  const percentageBar = {
    background: "#F8FAFC",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    color: "#111827",
    fontSize: "13px",
    fontWeight: 600,
    padding: "10px 14px",
    marginBottom: "12px",
  };

  const sectionScoring = useMemo(
    () =>
      buildAverageRatingFromSectionBlob({
        sectionScoreBlob,
        normalizedMonths,
        getMonthScore: getMonthScoreForGroup,
      }),
    [sectionScoreBlob, normalizedMonths]
  );

  const printRef = useRef(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    const contentEl = printRef.current;
    if (!contentEl || isDownloadingPdf) {
      return;
    }

    try {
      setIsDownloadingPdf(true);
      const html2pdfModule = await import("html2pdf.js");
      const html2pdf = html2pdfModule.default || html2pdfModule;
      const normalizedMonthsForFile = monthDisplay
        ? monthDisplay
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
        : "";
      const filenameSuffix = isProbationaryView
        ? `probationary-appraisal${normalizedMonthsForFile ? `-${normalizedMonthsForFile}` : ""}`
        : quarter
          ? `quarterly-appraisal-${String(quarter).toLowerCase()}`
          : "quarterly-appraisal";

      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename: `${filenameSuffix}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            scrollY: 0,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(contentEl)
        .save();
    } catch (error) {
      console.error("Error downloading quarterly appraisal PDF:", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div style={{ background: "#f5f6f8", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <h2 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "16px" }}>
        <span>
          {isProbationaryView
            ? `Probationary Appraisal Summary${monthDisplay ? ` - (${monthDisplay})` : ""}`
            : `Quarterly Appraisal Summary ${quarter ? `(${quarter})` : ""}`}
        </span>
      </h2>

      <div
        ref={printRef}
        style={{
          fontFamily: '"Segoe UI", "Open Sans", system-ui, sans-serif',
          color: "#111827",
          background: "#fff",
          padding: "20px 16px 28px",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
      <div
        className="mb-3"
        style={{
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
        }}
      >
        <div style={sectionBar}>Employee Information</div>
        <div
          style={{
            padding: "18px 16px 20px",
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "16px 24px",
          }}
        >
          <div>
            <div style={fieldLabel}>Employee Name</div>
            <div style={fieldBox}>{record.name || record.employee_name || "-"}</div>
          </div>
          <div>
            <div style={fieldLabel}>Employee Code</div>
            <div style={fieldBox}>{record.employee?.employee_code || "-"}</div>
          </div>
          <div>
            <div style={fieldLabel}>Position / Level</div>
            <div style={fieldBox}>
              {record.designation_name || record.designation || record.employee?.designation?.name || "-"}
            </div>
          </div>
          <div>
            <div style={fieldLabel}>Campaign</div>
            <div style={fieldBox}>{record.campaign || "-"}</div>
          </div>
          <div>
            <div style={fieldLabel}>Department</div>
            <div style={fieldBox}>{record.department || "-"}</div>
          </div>
          <div>
            <div style={fieldLabel}>Months</div>
            <div style={fieldBox}>{monthDisplay || "-"}</div>
          </div>
        </div>
      </div>

      <div
        className="mb-3"
        style={{
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
        }}
      >
        <div style={sectionBar}>Section Summary</div>
        <div style={{ padding: "16px", display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
          {getRenderableSectionScoreEntries(sectionScoreBlob).map(([groupName, groupData]) => {
              return (
                <div key={groupName} style={{ border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden" }}>
                  <div style={{ ...sectionBar, fontSize: "12px" }}>
                    {groupName} ({resolveSectionGroupWeightage(groupData)}%)
                  </div>
                  <div style={{ padding: "10px 12px" }}>
                    <div className="table-responsive">
                      <table
                        className="table mb-0"
                        style={{ borderColor: "#E5E7EB", width: "100%", tableLayout: "fixed" }}
                      >
                        <colgroup>
                          <col style={{ width: "70%" }} />
                          <col style={{ width: "30%" }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: "#F0F2F5" }}>
                            <th
                              style={{
                                borderColor: "#E5E7EB",
                                fontWeight: 600,
                                padding: "10px 12px",
                                textAlign: "left",
                              }}
                            >
                              Month
                            </th>
                            <th
                              style={{
                                borderColor: "#E5E7EB",
                                fontWeight: 600,
                                padding: "10px 12px",
                                textAlign: "right",
                              }}
                            >
                              Obtained Score
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthLabels.length > 0 ? (
                            normalizedMonths.map((monthKey, idx) => (
                              <tr key={monthKey}>
                                <td style={{ borderColor: "#E5E7EB", padding: "10px 12px" }}>
                                  {monthLabels[idx]}
                                </td>
                                <td style={{ borderColor: "#E5E7EB", padding: "10px 12px", textAlign: "right" }}>
                                  {formatAppraisalScoreDisplay(getMonthScoreForGroup(groupData, monthKey), "-")}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td style={{ borderColor: "#E5E7EB", padding: "10px 12px" }}>-</td>
                              <td style={{ borderColor: "#E5E7EB", padding: "10px 12px", textAlign: "right" }}>
                                {formatAppraisalScoreDisplay(groupData?.total_score, "-")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <AverageRatingCalculationTable
        resultsRows={sectionScoring.resultsRows}
        totalAppraisal={sectionScoring.totalAppraisal}
        overallScore={overallScore}
      />

      <div style={percentageBar}>
        {isProbationaryView ? "Probationary Appraisal Percentage" : "Quarterly Appraisal Percentage"}:{" "}
        <span style={{ color: "#0F172A" }}>
          {overallScore != null && overallScore !== "" ? `${overallScore}%` : "-"}
        </span>
      </div>
      </div>

      <div className="mt-3">
        <button type="button" className="c-b-btn me-2" onClick={() => navigate(-1)}>Cancel</button>
        <button type="button" className="n-u-btn" onClick={handleDownloadPdf} disabled={isDownloadingPdf}>
          {isDownloadingPdf ? "Downloading..." : "Download as PDF"}
        </button>
      </div>
      </div>
    </div>
  );
};

export default QuarterlyAppraisalView;
